/**
 * groupChatView.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import moment from "moment-timezone";
import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip
} from "@mui/material"

//import ClickAwayListener from "@mui/material/ClickAwayListener";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

//import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
//import MenuItem from "@mui/material/MenuItem";
//import MenuList from "@mui/material/MenuList";
//import Paper from "@mui/material/Paper";
//import Popper from "@mui/material/Popper";

import { AuthContext } from "../auth/auth.context";
import { GroupNavContext } from "../chat/groupNav.context";

import { AccountMemberSearchComponent } from "./accountMemberSearch.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { MemberTypeDropdown } from "../components/memberTypeDropdown.component";

import { Device, DeviceNode, Membership, PermissionLevel } from "../models/auth";
import { GroupMembership, GroupMemberType, GroupSubmissionType, GroupType, GroupPKI, Group, GroupState } from "../models/group";
import { authServiceFactory } from "../services/auth.service";

import { GroupService, groupServiceFactory } from "../services/group.service";

interface MembershipSelection {
  groupMember?: GroupMembership,
  acctMember: Membership,
  newMemberType: GroupMemberType,
  loadingDevices: boolean
}

interface DeviceSelection {
  selected: boolean,
  disabled: boolean,
  hasPubKey: boolean,
  wasAlreadyMember: boolean,
  device: Device
}

interface SuggestedRemoval {
  acctMember: Membership,
  device: Device
}

interface GroupMemberListComponentParams {
  groupID: string,
  [key: string]: string | undefined
}

export function GroupMemberListComponent(): React.ReactElement<any, any> {
  const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const authContext = React.useContext<AuthContext>(AuthContext);
  const { groupID } = useParams<GroupMemberListComponentParams>() as { groupID: string };
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [curGroupMembers, setCurGroupMembers] = React.useState<MembershipSelection[]>([]);
  const [newMembers, setNewMembers] = React.useState<Array<MembershipSelection>>([]);
  const [myDevice, setMyDevice] = React.useState<string>("");
  const [myGroupMembership, setMyGroupMembership] = React.useState<GroupMembership | null>(null);
  const [groupPKI, setPKI] = React.useState<GroupPKI | null>(null);
  const [deviceMap, setDeviceMap] = React.useState<{ [userID: string]: DeviceSelection[] }>({});
  const [existingMap, setExistingMap] = React.useState<{ [userID: string]: string[] }>({});
  const [memberTypeMenuAnchorEl, setMemberTypeMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedGroupMemberSel, setSelectedGroupMemberSel] = React.useState<MembershipSelection | null>(null);
  const [fullyWelcomed, setFullyWelcomed] = React.useState(false);

  const haveMemWritePermission = groupNavContext.can(PermissionLevel.WRITE, "members");
  const haveRatchetTree = myGroupMembership && myGroupMembership.haveRatchetTree || false;

  React.useEffect(function () {
    setLoading(true);
    if (!groupNavContext.groupMemberships &&
      !groupNavContext.directMessages) { return; }
    if (groupNavContext.groupMemberships.length <= 0 &&
      groupNavContext.directMessages.length <= 0) { return; }

    const membership: GroupMembership | undefined = groupNavContext.groupMembershipMap[groupID];

    if (!membership) {
      console.error("No membership with groupID:", groupID);
      setErrMsg("Group does not exist or you are not a member");
      setLoading(false);
      return;
    }
    if (membership.groupID !== groupNavContext.currentGroup?.groupID) {
      groupNavContext.setCurrentGroup(membership);
    }

    const groupService = groupServiceFactory();
    setMyDevice(groupService.deviceID);
    setMyGroupMembership(membership);

    bluebird.all([
      groupService.getGroupMembers(membership.accountID, membership.groupID),
      membership.haveRatchetTree ? groupService.getLocalGroup(membership.groupID) : bluebird.resolve(null)
    ])
      .then(([groupMembers, group]) => {
        setPKI(group?.pki || GroupPKI.p256);
        setFullyWelcomed(group && groupService.isFullyWelcomed(group) || false);
        const userIDs: Set<string> = new Set(groupMembers.map(m => m.userID));
        return authServiceFactory().membersFor(userIDs).then(acctMemberMap => {
          return { groupMembers, acctMemberMap };
        });
      }).then(({ groupMembers, acctMemberMap }) => {
        const arr: Array<MembershipSelection> = [];
        const existingMap: { [userID: string]: string[] } = {};
        for (const mem of groupMembers) {
          arr.push({
            groupMember: mem,
            acctMember: acctMemberMap[mem.userID],
            newMemberType: mem.groupMemberType,
            loadingDevices: false
          });
          existingMap[mem.userID] = [];
          for (const d of mem.devices) {
            existingMap[mem.userID].push(d._id);
          }
        }
        setCurGroupMembers(arr);
        setExistingMap(existingMap);

        // update any existing cached devices
        const newDevMap: { [userID: string]: DeviceSelection[] } = {};
        for (const userID in deviceMap) {
          const devSels = deviceMap[userID];
          for (const devSel of devSels) {
            const isAlreadyAdded = existingMap[userID] ?
              existingMap[userID].includes(devSel.device._id) : false;
            devSel.selected = isAlreadyAdded;
            devSel.wasAlreadyMember = isAlreadyAdded;
          }
          newDevMap[userID] = devSels;
        }
        setDeviceMap(newDevMap);
      })
      .then(() => {
        setLoading(false);
      }, err => {
        console.error("Error:", err);
        setErrMsg("Error fetching group state: " + err.message);
        setLoading(false);
      });

  }, [groupID, groupNavContext.groupMemberships.length]);

  async function fetchDevices(userID: string, memberSel: MembershipSelection): Promise<Array<DeviceSelection>> {
    const memberDevices = await authServiceFactory().getMemberDevices(userID);
    const sevenDaysAgo = moment().subtract(7, "days");

    const newSels = memberDevices.map(device => {
      const isAlreadyAdded = existingMap[userID] ?
        existingMap[userID].includes(device._id) : false;
      const shouldDisable = groupPKI === GroupPKI.p256 && !device.publicKey ||
        groupPKI === GroupPKI.crystals && (!device.kyberKey || !device.verifyKey);
      const modDate = device.modified || device.created;
      // if groupMembership already exists, the user is already a member, don't auto-select
      const selected = isAlreadyAdded ||
        (!memberSel.groupMember && modDate && modDate.isAfter(sevenDaysAgo)) || false;
      return {
        device: device,
        hasPubKey: (groupPKI === GroupPKI.p256 && device.publicKey ||
          groupPKI === GroupPKI.crystals && device.kyberKey && device.verifyKey) as boolean,
        disabled: shouldDisable,
        selected: selected,
        wasAlreadyMember: isAlreadyAdded
      };
    }).sort(function (a, b): number {
      if (a.device.modified?.isBefore(b.device.modified)) {
        return 1;
      } else if (b.device.modified?.isBefore(a.device.modified)) {
        return -1;
      }
      return 0;
    });
    setDeviceMap({
      ...deviceMap,
      [userID]: newSels
    });
    console.log("devices:", newSels);
    return newSels;
  }

  async function selectedNewMember(member: Membership) {
    if (newMembers.findIndex(m => m.acctMember?.userID === member.userID) >= 0) {
      return;
    }
    if (curGroupMembers.findIndex(m => m.groupMember?.userID === member.userID) >= 0) {
      return;
    }
    const groupMemberSel: MembershipSelection = {
      acctMember: member,
      newMemberType: myGroupMembership && myGroupMembership.submissiontype === GroupSubmissionType.broadcastOnly ?
        GroupMemberType.observer :
        GroupMemberType.user,
      loadingDevices: true
    };
    console.log("new member:", groupMemberSel);
    const newArr = newMembers.concat(groupMemberSel);
    setNewMembers(newArr);
    try {
      await fetchDevices(member.userID, groupMemberSel);
    } catch (err) {
      console.error("error loading member devices:", err);
    } finally {
      groupMemberSel.loadingDevices = false;
      const finishedArr = Array.from(newArr);
      setNewMembers(finishedArr);
    }
  }

  async function changedAccordion(groupMemberSel: MembershipSelection,
    _index: number, expanded: boolean): Promise<any> {
    if (!expanded) { return; }

    const userID = groupMemberSel.groupMember?.userID || groupMemberSel.acctMember?.userID;
    if (!userID) { return; }
    if (deviceMap[userID]) { return; }

    groupMemberSel.loadingDevices = true;
    // we fetch new members' devices immediately, so this must be a current member
    setCurGroupMembers(Array.from(curGroupMembers));
    try {
      await fetchDevices(userID, groupMemberSel);
    } catch (err) {
      console.error("Error loading member devices:", err);
    } finally {
      groupMemberSel.loadingDevices = false;
      setCurGroupMembers(Array.from(curGroupMembers));
    }
  }

  function changeSelection(userID: string, index: number) {
    const newDevices = [...deviceMap[userID]];
    const devSel = newDevices[index];
    if (groupPKI === GroupPKI.p256 && !devSel.device.publicKey ||
      groupPKI === GroupPKI.crystals && !devSel.device.kyberKey) {
      console.log("not selecting device. Doesn't support group's PKI:", devSel);
      devSel.selected = false;
    } else {
      devSel.selected = !devSel.selected;
    }
    setDeviceMap({
      ...deviceMap,
      [userID]: newDevices
    });
  }

  async function removeGroupMemberIfPossible(group: Group | null, userID: string,
    groupService: GroupService): Promise<void> {
    const curMembership = authContext.membership;
    if (!curMembership) {
      console.error("No current account membership for user");
      return;
    }
    if (group && group.treenodes) {
      let missingFromGroup = true;
      for (const node of group.treenodes) {
        if (!node.deviceID || !node.userID || node.userID !== userID) {
          continue;
        }
        missingFromGroup = false;
      }
      if (missingFromGroup) {
        // the current group has no nodes for this userID and all devices are removed
        // somehow, a group member still exists for this user, though. So we need to
        // remove it.
        await groupService.removeGroupMember(curMembership.accountID, groupID, userID);
      }
    }
  }

  async function updateGroup(groupPKI?: GroupPKI | null) {
    const curMembership = authContext.membership;
    if (!curMembership) {
      console.error("No current account membership for user");
      return;
    }

    let thisMember: MembershipSelection | null = null;
    let thisDevice: Device | null = null;
    const groupService = groupServiceFactory();
    const memberMap: { [userID: string]: MembershipSelection } = {};
    const adding: Array<DeviceSelection> = [];
    const leaving: Array<DeviceSelection> = [];
    const devices: Array<DeviceNode> = [];
    const newCurArr = Array.from(curGroupMembers);
    const newPending = Array.from(newMembers);
    const curGroupState = myGroupMembership?.groupstate || GroupState.active;
    let allMembersActive = true;

    const group = await groupService.getLocalGroup(groupID);

    for (let i = 0; i < curGroupMembers.length; i++) {
      const member = curGroupMembers[i];
      const userID = member.groupMember?.userID || member.acctMember?.userID;
      if (!userID) { continue; }
      if (userID === curMembership.userID) {
        thisMember = member;
      }
      memberMap[userID] = member;
      if (member.groupMember && member.groupMember.groupMemberType !== member.newMemberType) {
        await groupService.updateGroupMembershipType(curMembership.accountID, groupID, member.groupMember.userID,
          member.groupMember.groupname, member.newMemberType);
        member.groupMember.groupMemberType = member.newMemberType;
        if (myGroupMembership && myGroupMembership.userID === userID) {
          myGroupMembership.groupMemberType = member.newMemberType;
        }
      }
      let allRemovedFromMember = true;
      const deviceSelections = deviceMap[userID];
      if (!deviceSelections) {
        // if the account Membership wasn't found, then this user was removed from the account
        // and we shouldn't keep any devices around. So, if there's still an acctMember but
        // we didn't fetch the devices for them. Keep whatever devices are already on the group.
        if (!member.acctMember && (!member.groupMember || member.groupMember.grouptype !== GroupType.dm)) {
          newCurArr.splice(i, 1);
          await removeGroupMemberIfPossible(group, userID, groupService);
          allMembersActive = false;
          continue;
        }
        if (group && group.treenodes) {
          const deviceIDs: Array<string> = [];
          for (const node of group.treenodes) {
            if (!node.deviceID || !node.userID || node.userID !== userID) {
              continue;
            }
            deviceIDs.push(node.deviceID);
          }
          await bluebird.map(deviceIDs, async deviceID => {
            const device = await groupService.getDevice(deviceID);
            if (device && (device.isDead === undefined || !device.isDead)) {
              devices.push(device);
              allRemovedFromMember = false;
            }
          });
        }
        if (allRemovedFromMember) {
          allMembersActive = false;
          if (member.groupMember && member.groupMember.grouptype === GroupType.dm && myGroupMembership) {
            myGroupMembership.groupstate = GroupState.frozen;

          }
        }
        continue;
      }

      for (const devSel of deviceSelections) {
        if (devSel.selected) {
          adding.push(devSel);
          devices.push(devSel.device);
          allRemovedFromMember = false;
        } else if (devSel.wasAlreadyMember && devSel.device._id === myDevice) {
          thisDevice = devSel.device;
          adding.push(devSel); // keep this device for now, will remove after
          devices.push(devSel.device);
          allRemovedFromMember = false;
        } else {
          leaving.push(devSel);
        }
      }
      if (allRemovedFromMember) {
        allMembersActive = false;
        if (!member.groupMember || member.groupMember.grouptype !== GroupType.dm) {
          newCurArr.splice(i, 1);
          await removeGroupMemberIfPossible(group, userID, groupService);
        } else if (myGroupMembership) {
          // if DM, then the DM is now frozen
          myGroupMembership.groupstate = GroupState.frozen;
        }
      }
    }
    for (let i = (newMembers.length - 1); i >= 0; i--) {
      const member = newMembers[i];
      const userID = member.groupMember?.userID || member.acctMember?.userID;
      if (!userID) { continue; }
      memberMap[userID] = member;
      let atLeastOneDevParticipating = false;
      for (const devSel of deviceMap[userID]) {
        if (devSel.selected && !devSel.wasAlreadyMember) {
          devSel.device.groupMemberType = member.newMemberType;
          adding.push(devSel);
          devices.push(devSel.device);
          atLeastOneDevParticipating = true;
        }
      }
      newPending.splice(i, 1);  
      if (atLeastOneDevParticipating) {
        newCurArr.push(member);
      }
    }

    if (allMembersActive && curGroupState === GroupState.frozen && myGroupMembership) {
      myGroupMembership.groupstate = GroupState.active;
    }

    try {
      setSaving(true);
      await groupService.reWelcomeGroup(curMembership.accountID, groupID, devices, groupPKI);

      if (groupPKI) { setPKI(groupPKI); }

      for (const devSel of adding) {
        const member = memberMap[devSel.device.userID];
        if (!member) {
          console.warn("memberSelection not in memberMap for added device:", devSel);
          continue;
        }

        if (!member.groupMember) {
          const devMembers = deviceMap[member.acctMember.userID].map(devSel => {
            return {
              _id: devSel.device._id,
              atEpoch: 0,
              atCounter: 0
            };
          });
          member.groupMember = {
            accountID: curMembership.accountID,
            groupID: groupID,
            userID: member.acctMember.userID,
            groupname: myGroupMembership?.groupname || "",
            grouptype: myGroupMembership?.grouptype || GroupType.public,
            groupMemberType: member.newMemberType,
            submissiontype: myGroupMembership?.submissiontype || GroupSubmissionType.normal,
            archiving: myGroupMembership?.archiving || false,
            perms: {},
            devices: devMembers,
            created: moment()
          };
        }
        devSel.wasAlreadyMember = true;

        if (member.acctMember) {
          const index = newPending.findIndex(m => m.acctMember && m.acctMember.userID == member.acctMember?.userID);
          if (index >= 0) {
            newPending.splice(index, 1);
            newCurArr.push(member);
          }
        }
      }

      for (const devSel of leaving) {
        devSel.wasAlreadyMember = false;
        const existing = existingMap[devSel.device.userID];
        if (!existing) { continue; }
        for (let i = 0; i < existing.length; i++) {
          if (existing[i] === devSel.device._id) {
            existing.splice(i, 1);
            break;
          }
        }
      }

      if (thisDevice) {
        await groupService.removeDeviceFromGroup(curMembership.accountID, curMembership.userID,
          groupID, thisDevice.userID, thisDevice._id);

        const gMembership = thisMember?.groupMember;
        if (!gMembership) {
          console.warn("group membership missing on curGroupMember:", thisMember);
        } else {
          const index = gMembership.devices.findIndex(d => d._id === thisDevice?._id);
          if (index >= 0) {
            gMembership.devices.splice(index, 1);
          } else {
            console.warn("cur user:", gMembership.userID, "has no device with id:", thisDevice._id);
          }
          if (myGroupMembership) {
            myGroupMembership.haveRatchetTree = false;
            setFullyWelcomed(false);
          }
          console.log("saving my membership:", myGroupMembership)
          setMyGroupMembership(myGroupMembership);
        }
      }

      setCurGroupMembers(newCurArr);
      setNewMembers(newPending);
      setExistingMap(existingMap);
    } catch (err) {
      if (groupPKI) { 
        // throw to PkeSchemeChooser if error is with switching pki
        // otherwise it will show that the migration was successful
        throw err; 
      }
      console.error("Error updating group:", err);
      setErrMsg((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function checkForPQE(): Promise<[Array<SuggestedRemoval>, Set<string>]> {
    const neededRemovals: Array<SuggestedRemoval> = [];
    const group = await groupServiceFactory().getLocalGroup(groupID);
    if (!group || !group.treenodes) {
      throw new Error("Locally stored group missing. Is this device a member?");
    }
    const memberDeviceSet: Set<string> = new Set();
    for (let i = 0; i < group.treenodes.length; i += 2) {
      const node = group.treenodes[i];
      if (!node.deviceID) { continue; }
      if (node.deviceID === group._id) { continue; }
      memberDeviceSet.add(node.deviceID);
    }
    for (let i = 0; i < curGroupMembers.length; i++) {
      const member = curGroupMembers[i];
      const userID = member.groupMember?.userID || member.acctMember?.userID;
      if (!userID) { continue; }
      let deviceSelections = deviceMap[userID];

      if (!deviceSelections) {
        deviceSelections = await fetchDevices(userID, member);
      }
      for (const devSel of deviceSelections) {
        memberDeviceSet.delete(devSel.device._id);
        if (devSel.wasAlreadyMember && !devSel.device.kyberKey) {
          neededRemovals.push({
            acctMember: member.acctMember,
            device: devSel.device
          });
        }
      }
    }
    return [neededRemovals, memberDeviceSet];
  }

  function doOpenMemberTypeMenu(event: React.MouseEvent<HTMLButtonElement>, groupMemberSel: MembershipSelection) {
    event.stopPropagation();
    event.preventDefault();
    if (selectedGroupMemberSel && selectedGroupMemberSel === groupMemberSel) {
      setMemberTypeMenuAnchorEl(null);
      setSelectedGroupMemberSel(null);
    } else {
      setMemberTypeMenuAnchorEl(event.currentTarget);
      setSelectedGroupMemberSel(groupMemberSel);
    }
  }

  function setMemberType(groupMemberType: GroupMemberType) {
    if (!selectedGroupMemberSel) { return; }
    selectedGroupMemberSel.newMemberType = groupMemberType;
    setMemberTypeMenuAnchorEl(null);
    setSelectedGroupMemberSel(null);
  }

  function renderHeader(acctMember: Membership | undefined, groupMemberSel: MembershipSelection): React.ReactElement<any, any> {
    return <header className="group-member-header"
      data-user-id={acctMember?.userID || groupMemberSel.groupMember?.userID || ""}>
      {acctMember ?
        <h4>{acctMember.firstname} {acctMember.lastname}</h4> :
        <h4>Unknown</h4>
      }
      {haveMemWritePermission && myGroupMembership?.grouptype !== GroupType.dm ?
        <Button
          disableRipple disableTouchRipple
          onClick={e => doOpenMemberTypeMenu(e, groupMemberSel)}>
          {groupMemberSel.newMemberType}
        </Button> :
        <span className="group-member-type">{groupMemberSel.newMemberType}</span>
      }
    </header>;
  }

  return <div className="group-member-list-container" aria-busy={loading}>
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
        disableRipple disableTouchRipple
        onClick={groupNavContext.toggleGroupList}>
        <MenuIcon />
      </IconButton>
      <h3>Group Members: {groupNavContext.currentGroup?.groupname || ""}</h3>
      <Button size="small" onClick={() => updateGroup()}
        disableRipple disableTouchRipple disabled={saving || !fullyWelcomed}>
        {saving ? <CircularProgress size="15" /> : "Save"}
      </Button>
    </header>

    {!fullyWelcomed && !loading && <div className="pke-migration-container">
      <Alert severity="warning">
        {myGroupMembership?.devices.find(dev => dev._id === myDevice) ? 
          `This device has not received group secrets from another member yet.
          You can only change some settings until they have been received.` :
          `This device is not a member of this group. Some settings cannot be 
          changed until it has been added to the group.`}
      </Alert>
    </div>}
    {haveRatchetTree && fullyWelcomed && haveMemWritePermission && !loading && groupPKI !== null &&
      <PkeSchemeChooser groupPKI={groupPKI}
        checkPQEAvailability={checkForPQE}
        doPKIUpdate={pki => updateGroup(pki)} />}

    {haveRatchetTree && fullyWelcomed &&
      myGroupMembership && myGroupMembership.grouptype !== GroupType.dm &&
      haveMemWritePermission &&
      <AccountMemberSearchComponent className="member-search" onMemberSelected={selectedNewMember} />}

    {curGroupMembers.map((groupMemberSel, index) => {
      const groupMember = groupMemberSel.groupMember;
      if (!groupMember) { return; }

      const acctMember = groupMemberSel.acctMember;
      const canDelete = haveRatchetTree && fullyWelcomed &&
        (acctMember && acctMember.userID === groupNavContext.currentGroup?.userID ||
          fullyWelcomed && haveMemWritePermission);

      return <Accordion disableGutters
        elevation={0} square={true}
        key={groupMember.userID}
        className="group-member-accordion"
        data-user-id={groupMember.userID}
        onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
          changedAccordion(groupMemberSel, index, expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {renderHeader(acctMember, groupMemberSel)}
        </AccordionSummary>
        <AccordionDetails>
          <GroupMemberDeviceListComponent key={groupMember.userID}
            acctMember={acctMember}
            loadingDevices={groupMemberSel.loadingDevices}
            devices={deviceMap[groupMember.userID]}
            index={index}
            canDelete={canDelete}
            myDevice={myDevice}
            displayTZ={authContext.tz()}
            onChangedSelection={changeSelection}
          />
        </AccordionDetails>
      </Accordion>;
    })}

    {newMembers.map((acctMemberSel, index) => {
      const acctMember = acctMemberSel.acctMember;
      if (!acctMember) { return; }
      return <Accordion disableGutters elevation={0}
        className="group-member-accordion"
        square={true} key={acctMember.userID} defaultExpanded={deviceMap[acctMember.userID] !== undefined}
        onChange={(_event: React.SyntheticEvent, expanded: boolean) =>
          changedAccordion(acctMemberSel, index, expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {renderHeader(acctMember, acctMemberSel)}
        </AccordionSummary>
        <AccordionDetails>
          <GroupMemberDeviceListComponent key={acctMember.userID}
            acctMember={acctMember}
            loadingDevices={acctMemberSel.loadingDevices}
            devices={deviceMap[acctMember.userID]}
            index={index}
            canDelete={true}
            myDevice={myDevice}
            displayTZ={authContext.tz()}
            onChangedSelection={changeSelection}
          />
        </AccordionDetails>
      </Accordion>
    })}
    <MemberTypeDropdown
      anchor={memberTypeMenuAnchorEl}
      currentMemberType={selectedGroupMemberSel?.newMemberType}
      currentSubmissionType={myGroupMembership?.submissiontype}
      setMemberType={setMemberType}
      onClose={() => { setMemberTypeMenuAnchorEl(null); setSelectedGroupMemberSel(null); }} />
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
  </div>;
}

interface GroupMemberDeviceListComponentProps {
  acctMember: Membership,
  index: number,
  canDelete: boolean,
  myDevice: string,
  loadingDevices: boolean,
  devices: Array<DeviceSelection> | undefined,
  displayTZ: string,
  onChangedSelection(userID: string, index: number): void
}

function GroupMemberDeviceListComponent(props: GroupMemberDeviceListComponentProps): React.ReactElement<any, any> {
  return <article className="group-member-device-list-container">
    {props.loadingDevices ? <CircularProgress size={20} /> :
      props.devices?.map((devSel, index) => {
        let displayString = devSel.device.displayString || devSel.device._id;
        if (devSel.device._id === props.myDevice) {
          displayString += " (This Device)";
        }
        if (props.displayTZ) {
          devSel.device.modified?.tz(props.displayTZ);
        }

        let classNames = "group-member-device-container";
        if (devSel.device.kyberKey) { classNames += " with-pqe"; }
        if (devSel.disabled) { classNames += " disabled"; }
        if (!devSel.hasPubKey) { classNames += " missing-pub-key"; }

        return <div className={classNames}
          key={devSel.device._id} data-device-id={devSel.device._id}>
          <FormControlLabel control={
            <Checkbox color="secondary"
              disableFocusRipple={true}
              disableRipple={true}
              disabled={devSel.disabled || !props.canDelete}
              checked={devSel.selected}
              onChange={() => props.onChangedSelection(props.acctMember.userID, index)} />}
            label={<div>
              <h5 className="device-name">
                <span>{displayString}</span>
                <Tooltip title="Post-Quantum Encryption(PQE) Capable">
                  <i className="fa-solid fa-atom pqe-indicator" />
                </Tooltip>
              </h5>
              {devSel.device.modified && <span className="desc">Last Seen: {devSel.device.modified.format("MMM D, YYYY h:mma")}</span>}
            </div>}
          />
        </div>;
      })}
  </article>;
}

interface PkeProps {
  groupPKI: GroupPKI,
  checkPQEAvailability(): Promise<[Array<SuggestedRemoval>, Set<string>]>,
  doPKIUpdate(newPKI: GroupPKI): Promise<void>
}

function PkeSchemeChooser(props: PkeProps): React.ReactElement<any, any> {
  const [checkingPKE, setChecking] = React.useState<boolean>(false);
  const [canMigrate, setCanMigrate] = React.useState<boolean>(false);
  const [haveDeletions, setHaveDeletions] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [neededRemovals, setNeededRemovals] = React.useState<Array<SuggestedRemoval>>([]);
  const [errMsg, setErrMsg] = React.useState("");

  async function doCheckAvailability() {
    setChecking(true);
    setCanMigrate(false);
    setNeededRemovals([]);

    try {
      const [removals, unknownDevicesToRemove] = await props.checkPQEAvailability();
      setNeededRemovals(removals);
      if (removals.length <= 0 && unknownDevicesToRemove.size <= 0) {
        console.log("can migrate:", removals, unknownDevicesToRemove);
        setCanMigrate(true);
        setHaveDeletions(false);
        return;
      }
      console.log("need to remove some devices:", removals, unknownDevicesToRemove);
      setHaveDeletions(true);
    } catch (err) {
      console.error("Error processing group:", err);
      setErrMsg((err as Error).message);
    } finally {
      setChecking(false);
    }
  }

  async function doCrystalsMigration() {
    try {
      await props.doPKIUpdate(GroupPKI.crystals);
      setSuccess(true);
    } catch (err) {
      console.error("Error setting pki:", err);
      setErrMsg((err as Error).message);
    }

  }

  async function doEccMigration() {
    try {
      await props.doPKIUpdate(GroupPKI.p256);
      setSuccess(true);
    } catch (err) {
      console.error("Error setting pki:", err);
      setErrMsg((err as Error).message);
    }
  }

  function showRemoveUI(): React.ReactElement<any, any> {
    return <>
      <AlertTitle>We can't migrate just yet.</AlertTitle>
      {neededRemovals.length > 0 ? <>
        <p>If it makes sense, please, remove the following devices:</p>
        <ul>
          {neededRemovals.slice(0, 10).map(dev => {
            return <li key={dev.device._id}>
              <span>{dev.device.displayString || "unknown"}</span>
              <span> - </span>
              <span>{dev.acctMember.firstname} {dev.acctMember.lastname}</span>
            </li>;
          })}
          {neededRemovals.length > 10 && <li>...</li>}
        </ul>
      </> : <>
        <p>Some deleted devices still have membership in the group.
          To remove them, just save changes. Then try again.</p>
      </>}
    </>;
  }

  function showMigrationUI(): React.ReactElement<any, any> {
    return <>
      <AlertTitle>All devices check out!</AlertTitle>
    </>;
  }

  function switchToCrystalsUI(): React.ReactElement<any, any> {
    return <>
      <AlertTitle>Experimental: Try out Post Quantum Encryption (PQE)?</AlertTitle>
      {checkingPKE && <span>Analyzing Devices...</span>}
    </>;
  }

  function switchToEccUI(): React.ReactElement<any, any> {
    return <>
      <AlertTitle>Switch back to Elliptic Curve PKE?</AlertTitle>
    </>;
  }

  function successUI(): React.ReactElement<any, any> {
    const text = props.groupPKI === GroupPKI.p256 ? "Success!" : "Success! Welcome to the future of encryption!";
    return <>
      <AlertTitle>{text}</AlertTitle>
    </>;
  }

  let actionText = "Check";
  let action: React.MouseEventHandler<any> | null = doCheckAvailability;
  if (errMsg) {
    action = null;
  } else if (success) {
    action = null;
  } else if (canMigrate) {
    actionText = "Migrate";
    action = doCrystalsMigration;
  } else if (haveDeletions) {
    actionText = "Retry";
  } else if (props.groupPKI === GroupPKI.crystals) {
    actionText = "Switch";
    action = doEccMigration;
  }

  return <div className="pke-migration-container">
    <Alert severity={errMsg ? "error" : "info"}
      icon={<i className="fa-solid fa-atom" />}
      action={action ? <Button size="small" onClick={action}>{actionText}</Button> : undefined}>
      {errMsg ? <p>{errMsg}</p> :
        success ? successUI() :
          canMigrate ? showMigrationUI() :
            haveDeletions ? showRemoveUI() :
              props.groupPKI === GroupPKI.p256 ? switchToCrystalsUI() :
                switchToEccUI()}
    </Alert>
  </div>;
}
